/* ══════════════════════════════════════════════════════════════════════════════════════════════════
   MNL — CONVERSION DE DOSE. UNE SEULE. V1271 — 06/08/2026.
   ══════════════════════════════════════════════════════════════════════════════════════════════════

   CE FICHIER EST LA SOURCE UNIQUE. Il existe en DEUX exemplaires OCTET POUR OCTET IDENTIQUES :
       assets/mnl-dose-conversion.js                    (thème Shopify — CDN Shopify)
       backend-vercel/public/mnl-dose-conversion.js     (Vercel — origine mynutrilab-ai.vercel.app)
   Les deux origines ne peuvent pas partager un fichier servi ; elles partagent donc le MÊME TEXTE.

   ── V1259 : POURQUOI « OCTET POUR OCTET » N'ÉTAIT GARANTI PAR RIEN, ET CE QUI LE GARANTIT ─────────
   Jusqu'ici la seule chose qui vérifiait l'égalité des deux copies était un banc croisé écrit dans un
   DOSSIER TEMPORAIRE : il disparaît avec la session, il ne tourne pas au déploiement, et un `shopify
   theme push` sans `vercel --prod` rouvrait la divergence EN SILENCE — c'est la panne que quatre
   passes ont poursuivie. L'empreinte V1258 (FINGERPRINT) ne suffisait pas non plus : elle est calculée
   SUR LES TABLES SEULEMENT. Deux copies dont les tables sont identiques mais dont `fromMg` diffère
   d'une ligne affichent la MÊME empreinte. Or c'est exactement l'histoire de ce dépôt : onze passes
   ont réécrit des ALGORITHMES, pas des tables.

   TROIS GARANTIES, AUCUNE DANS UN DOSSIER TEMPORAIRE :
     1. SIGNATURE (ci-dessous) — empreinte calculée À L'EXÉCUTION sur LE TEXTE SOURCE COMPLET de la
        fabrique, tables ET algorithme, via `String(MNL_DOSE_MODULE)`. Le seul texte qu'elle ignore est
        ce commentaire d'en-tête, qui est hors de la fabrique : la prose peut diverger, jamais le
        comportement. Deux pages qui n'affichent pas la même SIGNATURE ne calculent pas le même prix,
        et ça se constate depuis la console de n'importe quelle page, sans accès au dépôt.
     2. verif-module-unique.mjs (racine du thème, VERSIONNÉ) — compare les deux fichiers octet par
        octet, charge les deux, compare leurs SIGNATURE et FINGERPRINT, rejoue selfTest() sur les deux
        et sort en code ≠ 0 à la moindre divergence. `npm run verify` depuis backend-vercel/.
     3. atelier-conversion.html (Vercel) — charge la copie Vercel ET la copie Shopify, dans la même
        page, depuis leurs DEUX origines réelles, et affiche vert/rouge. C'est la seule vérification
        qui porte sur ce qui est DÉPLOYÉ et non sur ce qui est écrit sur le disque du fondateur.
   Et côté vente : assets/mnl-dose-guard.js refuse de charger une formule ANCIENNE si le module manque
   ou si son autotest échoue (défaut B — voir l'en-tête de ce fichier-là).

   ── POURQUOI CE FICHIER EXISTE ────────────────────────────────────────────────────────────────────
   Onze implémentations de conversion vivaient dans le dépôt, écrites par onze passes différentes,
   aucune n'appelant les autres. Trois passes de correction ont échoué de la même façon : chacune a
   réparé les portes qu'elle voyait. La cause n'était pas « une porte manquée », c'était l'absence de
   conversion UNIQUE. Tant que chaque surface porte la sienne, la suivante trouvera la divergence
   suivante. Ici il n'y a qu'un algorithme et qu'un jeu de tables ; toute surface qui convertit une
   dose DOIT appeler MNL_DOSES et ne rien recopier.

   ── L'ARBITRAGE : REPORT D'INDICE, PAS RÉÉCHELONNEMENT DE RANG ─────────────────────────────────────
   Deux algorithmes coexistaient pour la même donnée :
     (A) le configurateur REPORTAIT LE RANG :  ni = round(i / (n-1) * (m-1))
     (B) recharge.html RETROUVAIT LA VALEUR dans l'archive puis reportait SON indice.
   (B) est juste, (A) est faux, et ça se démontre sans invoquer l'élégance — par le seul critère qui
   compte : QUELLE DOSE LE CLIENT A-T-IL PAYÉE QUAND LE POT A ÉTÉ FABRIQUÉ ?

   PREUVE 1 — LES ACTIFS DONT L'ÉCHELLE N'A JAMAIS CHANGÉ DE VALEUR.
   La citrulline vaut 3 g au cran 1 de la grille v2 ; elle vaut TOUJOURS 3 g aujourd'hui (cran 2 de la
   grille courante, qui a gagné un cran bas à 2 g en V1079). Le pot contient 3 g, l'étiquette imprime
   3 g, le client a payé 3 g. Toute conversion qui rend autre chose est fausse par simple inspection.
   Or (A) rend round(1/4 × 5) = 1, soit 2 g : UN CRAN DE MOINS. Idem bêta-alanine v2 cran 1 (1 g →
   0,5 g), électrolytes (400 → 200 mg), taurine v2 cran 1 (500 mg → 125 au lieu de 250). Même défaut,
   même cause.
   ⚠ V1271 — CET EXEMPLE ÉTAIT PORTÉ PAR LA TYROSINE JUSQU'AU 06/08/2026, et il ne peut plus l'être :
   son échelle a été divisée par deux ce jour-là, donc « 500 mg au cran 1 de la v2 vaut toujours
   500 mg » est devenu FAUX (le cran 2 d'aujourd'hui vaut 250 mg). Ce n'est pas une régression de la
   conversion, c'est l'arbitrage fondateur qui s'applique : commande passée = recette gelée · rachat
   par QR = les changements sont MONTRÉS. La preuve, elle, tient toujours — elle a seulement changé de
   porteur, parce qu'un invariant qu'on abaisse cesse d'être un invariant. La leçon vaut pour la
   suite : ne jamais adosser une démonstration à une grille qui peut bouger.
   LA CAUSE : la V1079 n'a pas ÉTIRÉ les grilles, elle a INSÉRÉ DES CRANS PAR LE BAS. Un rang relatif
   suppose un étirement uniforme ; l'histoire réelle est une insertion. Le rang est donc l'invariant
   qu'il ne fallait pas choisir.

   PREUVE 2 — LES TABLES QUE LE FONDATEUR A LUI-MÊME DÉCRÉTÉES.
   V1230, glycérol v2 → grille du jour : « 3→1,5 · 5→2 · 6→3 · 8→4 » sur [0,1,1.5,2,3,4], soit les
   indices 2, 3, 4, 5. (A) rend pour le cran 1 (3 g) : round(1/4 × 5) = 1. Le fondateur dit 2.
   (B) retrouve 3 g dans l'univers à six crans [0,2,3,5,6,8] : indice 2. Le fondateur dit 2.
   Même verdict sur la v1 (« 6→3 » = indice 4 ; (A) rend 2).

   PREUVE 3 — LES ARCHIVES NON INVERSIBLES TRANCHENT L'AUTRE MOITIÉ DE LA QUESTION.
   Certaines archives d'ENCODEUR portent plusieurs crans à la même valeur : pinksalt [0,200,250,500,
   500,500] (trois crans à 500 mg), creatine [0,3,3,3,3,3] (cinq crans à 3 g). Une recherche PAR VALEUR
   y rend toujours le premier cran trouvé : quatre crans de créatine s'effondrent sur un seul. Donc :
   QUAND L'INDICE EST CONNU (QR ?r=, dont le 4ᵉ chiffre porte la génération), ON UTILISE L'INDICE LU,
   JAMAIS UN INDICE RECONSTRUIT DEPUIS LA VALEUR. La recherche par valeur ne sert qu'au raccordement
   v1/v2 → univers à six crans, où les grilles concernées sont strictement croissantes.

   ── L'ALGORITHME, EN ENTIER ───────────────────────────────────────────────────────────────────────
   RÉGIME 1 — GÉNÉRATION CONNUE (QR ?r=) : fromIndex(k, idx, gen)
       gen ≥ 3 : la géométrie est celle d'aujourd'hui → REPORT D'INDICE LITTÉRAL sur CURRENT.
       gen ≤ 2 : la grille n'a que 4 ou 5 crans → on lit la VALEUR du cran dans GRIDS[gen], on la
                 RETROUVE dans UNIV (l'univers à six crans de l'ère v3), et on reporte CET indice.
       Puis : plancher à 1 (un actif présent sur le pot ne disparaît pas) et sommet à sommet.
   RÉGIME 2 — GÉNÉRATION INCONNUE (?f=, localStorage, _recharge, clavier) : fromMg(k, mg)
       mg ≤ plafond du jour  → RENDU INTACT. Ancienneté non prouvée, ambiguïté assumée. C'est aussi
                               ce qui garantit qu'une formule NEUVE traverse sans rien subir et que le
                               plafond de l'appelant garde toute sa force.
       mg > plafond du jour  → ancienneté CERTAINE. On prend l'échelle la plus RÉCENTE capable de
                               porter la valeur (v4, puis v3, puis UNIV, puis v2, puis v1), on y
                               cherche le cran le plus proche, on reporte son indice (via UNIV pour
                               v1/v2). Au-dessus de TOUTE échelle jamais imprimée : dernier cran.
   Les deux régimes sont IDEMPOTENTS : le résultat est toujours un cran de la grille courante, donc
   ≤ plafond, donc rendu intact au second passage.

   ── CE QUE CE FICHIER NE FAIT PAS ─────────────────────────────────────────────────────────────────
   Il ne remplace PAS le plafond produit. clampLoadedDoses (configurateur), INGREDIENTS[k].max, MAX_R
   (recharge) restent en place et s'appliquent APRÈS. Une formule NEUVE hors bornes est toujours
   ramenée sous le plafond : la conversion ne la touche pas (régime 2, valeur ≤ plafond → intacte), le
   plafond fait son travail. L'ORDRE EST NON INTERCHANGEABLE : conversion d'abord, plafond ensuite.
   Écrêter d'abord détruit l'indice d'origine, et c'est l'indice qui est facturé.

   ⚠ NE JAMAIS RÉORDONNER KEYS NI UNE GRILLE. Les douze positions et les index sont ceux des QR
   IMPRIMÉS sur des pots physiques. La clé « betaine » RESTE en position 7 alors que l'actif est sorti
   du produit (V1135) : la retirer décalerait tous les chiffres suivants et ferait mentir chaque pot
   en circulation. Sa dose de sortie est forcée à 0 partout.

   ── V1271 (06/08/2026) — LE FORMAT DU CODE COMPACT S'ÉTEND, IL NE SE RÉÉCRIT PAS ──────────────────
   LE MUR : le 4e chiffre vaut 2×(génération−1) + sansStim. Un chiffre décimal, dix valeurs, toutes
   attribuées depuis la v5 (8/9). La v6 vaudrait 10 : le champ est LITTÉRALEMENT saturé, et trois
   commentaires du dépôt (configurateur, recharge.html, labels.js) le disent depuis la V1253 en
   ajoutant « élargir le format AVANT de bouger une grille ».

   CE QUE LE BUMP ACHÈTE, ET C'EST TOUT — mais ça compte. La conversion à INDICE CONSTANT fait qu'un
   pot v5 et un pot v6 au même cran RACHÈTENT la même dose (arbitrage fondateur : « rachat par QR =
   les changements sont MONTRÉS »). Ce que la génération donne, c'est de savoir AU SCAN ce que le pot
   qu'on tient CONTIENT : 2 g de tyrosine s'il est sorti avant, 1 g après. Sans elle, cette
   information est DÉTRUITE pour tout pot sortant à partir d'aujourd'hui — l'atelier ne peut plus
   distinguer deux pots au même chiffre 3, et l'arbitrage devient inapplicable faute de savoir de
   quoi on part.

   LE FORMAT, EN DEUX LANGAGES DE LONGUEURS DISJOINTES :
     HÉRITÉ  — 16 chiffres, INCHANGÉ À JAMAIS. Génération = ⌊chiffre 3 ÷ 2⌋ + 1, soit v1…v5.
     ÉTENDU  — les 16 MÊMES chiffres + la génération en décimal AJOUTÉE À LA FIN (gen ≥ 6).
   Le discriminant est la LONGUEUR, jamais une valeur : deux langages de longueurs disjointes ne
   peuvent pas se confondre, dans aucun sens. Lecture : len = 16 → hérité ; len ≥ 17 → étendu, gen =
   parseInt(suffixe), REFUS si < 6 (un suffixe qui annonce une génération déjà exprimable par le
   chiffre 3 ment sur sa propre nature — on ne devine pas, on rejette).

   POURQUOI LE CHIFFRE EST AJOUTÉ À LA FIN ET PAS AU DÉBUT — c'est le seul point de conception qui se
   paie comptant. Le renifleur d'URL de recharge.html est NON ANCRÉ (`/[?&]r=(\d{16})/`) : sur un code
   étendu il capture les 16 PREMIERS chiffres et obtient un code hérité BIEN FORMÉ, dont les douze
   indices sont justes. Un préfixage produirait, lui, un décalage pur : douze doses fausses, sans le
   moindre signal. La dégradation est donc JUSTE côté atelier et VIDE côté configurateur (son test
   `^[0-9]{16}$` échoue et decodeCompact rend null) — jamais fausse, sur aucune des deux surfaces.

   ⚠ LE CHIFFRE 3 EST GELÉ À 8 + sansStim, POUR TOUJOURS. C'est un SABOT DE COMPATIBILITÉ, pas un
   oubli : c'est lui qui fait que le code tronqué par le renifleur ancien reste un v5 bien formé.
   L'incrémenter n'est pas seulement impossible (saturé), ce serait contre-productif.
   ══════════════════════════════════════════════════════════════════════════════════════════════════ */
(function (root, factory) {
  var api = factory();
  try { root.MNL_DOSES = api; } catch (_) {}
  try { if (typeof module === 'object' && module.exports) module.exports = api; } catch (_) {}
}(typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : this), function MNL_DOSE_MODULE() {
  /* ⚠ LA FABRIQUE EST NOMMÉE, ET CE NOM EST LOAD-BEARING. `MNL_DOSE_MODULE` est le nom d'une
     expression de fonction : il n'existe QUE dans la portée de la fonction elle-même, il ne fuit pas
     dans le global, et c'est ce qui permet à sourceSignature() de lire son PROPRE texte source sans
     `arguments.callee` (interdit en mode strict). Le renommer ou le supprimer casse la SIGNATURE. */
  'use strict';

  /* Ordre POSITIONNEL du code compact 16 chiffres. FIGÉ par les QR imprimés. */
  var KEYS = ['caffeine', 'theanine', 'tyrosine', 'citrulline', 'glycerol', 'pinksalt',
              'betaalanine', 'betaine', 'taurine', 'creatine', 'electrolytes', 'piperine'];

  /* Actifs sortis du produit : dose de sortie forcée à 0, quelle que soit la porte et la génération.
     La CLÉ reste, la DOSE part. (bétaïne — V1135/V1136.) */
  var RETIRED = { betaine: true };

  /* ── LA GÉNÉRATION EN COURS DE FABRICATION ────────────────────────────────────────────────────────
     UN SEUL ENDROIT DANS TOUT LE DÉPÔT. Les encodeurs l'écrivent (codeAvecGen), les décodeurs la
     bornent (fromIndex), l'empreinte la couvre, et le jour où elle passe à 7 rien d'autre ne bouge.
     ⚠ À partir de 6, elle N'EST PLUS exprimable par le 4e chiffre (saturé) : elle voyage en SUFFIXE
       du code compact. Voir le bloc « V1271 » de l'en-tête avant d'y toucher. */
  var GEN_COURANTE = 6;

  /* ── GRILLE COURANTE (génération v6, code ÉTENDU — suffixe « 6 ») ──────────────────────────────────
     Miroir EXACT de PALIERS (assets/preworkout-configurator-v2.js), PALIERS_R (recharge.html) et
     PAL_R (api/labels.js). Le dernier cran EST le plafond produit : aucun plafond n'est écrit ailleurs
     dans ce fichier, capOf() le dérive. Le banc échoue si un littéral de plafond diverge d'ici.
     ⚠ V1271 : C'EST LA SEULE TABLE QUI CHANGE QUAND UN PLAFOND BAISSE. G5 juste dessous est désormais
       une ARCHIVE — la photographie de ce que contiennent les pots imprimés jusqu'au 06/08/2026 — et
       elle ne bougera plus JAMAIS. La passe qui abaisse la tyrosine écrit ICI, à indice constant
       (même nombre de crans, même position), et nulle part ailleurs. */
  var G6 = {
    caffeine:     [0, 50, 75, 100, 150, 200],
    theanine:     [0, 25, 50, 100, 150, 200],
    /* ═══ V1271 (06/08/2026, décision fondateur) — L-TYROSINE : PLAFOND 2000 → 1000 mg ═══
       Sortante [0, 250, 500, 1000, 1500, 2000] → nouvelle [0, 125, 250, 500, 750, 1000]. SIX crans
       avant, SIX crans après, cran par cran : 250→125 · 500→250 · 1000→500 · 1500→750 · 2000→1000,
       soit exactement la moitié. CONVERSION À INDICE CONSTANT, JAMAIS UN ÉCRÊTAGE — écrêter à 1000
       ferait passer une formule de l'indice 3 (dose moyenne) à l'indice 5 (le cran le plus cher) : le
       client paierait plus pour moins de tyrosine. La règle vaut AUSSI pour les doses déjà sous le
       nouveau plafond : 1000 mg n'est pas « déjà conforme », c'est l'indice 3 de l'échelle sortante,
       qui devient 500 mg. L'échelle remplacée est archivée juste dessous, dans G5.
       ⚠ CE QUI BOUGE, ET DANS QUEL SENS. L'INDICE ne bouge pour personne : le cran payé est le même.
       Le coût matière, lui, se calcule AUX GRAMMES dans mnlFormulaCost (seul le glycérol est pondéré
       par indice) : à 25,20 €/kg, diviser la dose par deux fait baisser le coût de 0,756 € au maximum
       sur un pot de 30 doses. autoDetectPalier étant croissant en coût, le palier de prix fin ne peut
       que DESCENDRE d'un cran ou rester — jamais monter.
       ⚠ V1278 (07/08/2026) — CE CHIFFRE ÉTAIT FAUX, ET TROIS CHIFFRES CIRCULAIENT POUR LA MÊME PHRASE.
       Cette ligne annonçait « 4 descendent, 21 ne bougent pas, 0 monte ». Le rapport de la passe qui a
       posé la grille en annonçait « 1 seule (MNL-1010) », un contrôle ultérieur « 7 ». MESURÉ À NOUVEAU,
       en rejouant mnlFormulaCost + autoDetectPalier EXTRAITS du moteur sur les 25 commandes réelles de
       backend-vercel/banc-commandes-reelles.json : 1 DESCEND (MNL-1010, high → mediumplus,
       16,3497 € → 15,5937 €), 24 NE BOUGENT PAS, 0 MONTE. Les 25 portent de la tyrosine et les 25
       voient leur coût matière baisser ; c'est le PALIER qui ne bouge que pour une.
       D'OÙ VENAIT LE 4, ET POURQUOI CE N'EST PAS UNE ERREUR DE CALCUL : la table de correspondance
       « nom d'actif affiché → clé » utilisée pour relire les commandes ignorait les libellés COURTS que
       le Coach écrit (« Caféine », « Sel rose », « Béta-Alanine », « Créatine », « Électrolytes »). Les
       six commandes issues du Coach perdaient la moitié de leurs actifs, leur coût s'effondrait, et
       trois d'entre elles traversaient une borne de palier POUR CETTE SEULE RAISON. Reproduit à
       l'identique : table amputée ⇒ 4 (MNL-1009, 1010, 1014, 1024) ; table complète ⇒ 1.
       UNE CLÉ MANQUANTE NE PRODUIT PAS UNE ERREUR, ELLE PRODUIT UN CHIFFRE PLAUSIBLE. C'est ce qui rend
       ce genre d'écart si durable : rien ne rougit, la valeur reste dans le bon ordre de grandeur, et
       elle se recopie de commentaire en commentaire. Le contrôle vit maintenant dans
       banc-theme/banc-v1271.cjs §4 (PALIERS_RACHAT, liste FIGÉE d'une entrée) : le chiffre n'est plus
       une phrase, c'est une assertion qui rougit si elle change. */
    tyrosine:     [0, 125, 250, 500, 750, 1000],
    citrulline:   [0, 2, 3, 4, 6, 8],
    glycerol:     [0, 0.5, 1, 1.5, 2, 3],
    pinksalt:     [0, 200, 250, 300, 400, 500],
    betaalanine:  [0, 0.5, 1, 2, 3, 4],
    betaine:      [0],                              /* retiré du produit — la clé reste, la dose est 0 */
    taurine:      [0, 125, 250, 500, 750, 1000],
    creatine:     [0, 1, 2, 3],                     /* V1131 : 4 crans */
    electrolytes: [0, 200, 400, 600, 1000, 1500],
    piperine:     [0, 0.5, 1, 1.5, 2]               /* V1134 : bridage ANSES */
  };

  /* ── ARCHIVE v5 (marqueur 8/9) — pots du 05/08 au 06/08/2026 ──────────────────────────────────────
     GELÉE PAR LA V1271, le jour où la génération v6 est ouverte. Elle était la grille courante jusque
     là ; à partir d'ici c'est une photographie, au même titre que G4/G3/G2/G1, et elle ne se modifie
     JAMAIS. C'est elle qui dit ce qu'un pot marqué 8/9 CONTIENT réellement. */
  var G5 = {
    caffeine:     [0, 50, 75, 100, 150, 200],
    theanine:     [0, 25, 50, 100, 150, 200],       /* V1236 : avant [0,50,100,200,300,400] */
    tyrosine:     [0, 250, 500, 1000, 1500, 2000],  /* SEULE ligne qui diffère de G6 — échelle gelée avant l'abaissement V1271 */
    citrulline:   [0, 2, 3, 4, 6, 8],
    glycerol:     [0, 0.5, 1, 1.5, 2, 3],           /* V1236 : avant [0,1,1.5,2,3,4] */
    pinksalt:     [0, 200, 250, 300, 400, 500],     /* V1171 : avant [0,200,250,500,750,1000] */
    betaalanine:  [0, 0.5, 1, 2, 3, 4],
    betaine:      [0],                              /* retiré du produit — la clé reste, la dose est 0 */
    taurine:      [0, 125, 250, 500, 750, 1000],    /* V1246 : avant [0,250,500,1000,1500,2000] */
    creatine:     [0, 1, 2, 3],                     /* V1131 : 4 crans */
    electrolytes: [0, 200, 400, 600, 1000, 1500],
    piperine:     [0, 0.5, 1, 1.5, 2]               /* V1134 : bridage ANSES */
  };

  /* ── ARCHIVE v4 (marqueur 6/7) — pots du 04/08 au 05/08/2026 ──────────────────────────────────────
     SEULE la taurine diffère de la v5. Photographie, jamais réécrite. */
  var G4 = {
    caffeine:     [0, 50, 75, 100, 150, 200],
    theanine:     [0, 25, 50, 100, 150, 200],
    tyrosine:     [0, 250, 500, 1000, 1500, 2000],
    citrulline:   [0, 2, 3, 4, 6, 8],
    glycerol:     [0, 0.5, 1, 1.5, 2, 3],
    pinksalt:     [0, 200, 250, 300, 400, 500],
    betaalanine:  [0, 0.5, 1, 2, 3, 4],
    betaine:      [0, 50, 100, 200, 300, 400],
    taurine:      [0, 250, 500, 1000, 1500, 2000],  /* gelée avant l'abaissement V1246 */
    creatine:     [0, 1, 2, 3],
    electrolytes: [0, 200, 400, 600, 1000, 1500],
    piperine:     [0, 0.5, 1, 1.5, 2]
  };

  /* ── ARCHIVE v3 (marqueur 4/5) — pots de la V1079 au 04/08/2026 ───────────────────────────────────
     Diffère de la v4 par la théanine (plafond 400) et le glycérol (plafond 4 g).
     ⚠ AMBIGUÏTÉ IRRÉDUCTIBLE, ARBITRAGE FONDATEUR OUVERT — lire avant de « corriger » trois colonnes :
     le marqueur v3 n'a PAS bougé pendant que trois grilles changeaient sous lui (créatine V1131,
     pipérine V1134, sel rose V1171). Pire, l'ENCODEUR et le DÉCODEUR n'ont pas eu la même table pour
     ces trois actifs jusqu'au 06/08/2026 : api/labels.js encodait sur pinksalt [0,100,200,300,400,500]
     · creatine [0,3,3,3,3,3] · piperine [0,3,5,8,10,15], recharge.html sur pinksalt
     [0,200,250,500,500,500] · mêmes créatine/pipérine, pendant que le configurateur — qui AFFICHE et
     qui FACTURE — lisait déjà [0,200,250,300,400,500] · [0,1,2,3] · [0,0.5,1,1.5,2].
     CE QUI EST RETENU ICI : la grille CLIENT, pour trois raisons cumulées et vérifiables.
       (1) C'est elle qui a affiché la formule et calculé le prix payé. Le critère est « quel indice le
           client a-t-il PAYÉ », pas « quel indice l'atelier a-t-il écrit ».
       (2) Elle est INVERSIBLE (strictement croissante) ; les deux tables d'encodeur ne le sont pas —
           [0,3,3,3,3,3] écrase quatre crans sur un. Les brancher rendrait justes les pots d'un indice
           et faux ceux d'un autre : une erreur échangée contre une autre.
       (3) Les colonnes d'encodeur sont documentées comme des DÉFAUTS et déjà corrigées (V1255 dans
           api/labels.js, V1256 dans recharge.html). Les graver dans une archive gèlerait un bug.
     RÉSIDU NON RÉPARABLE, À DIRE AU FONDATEUR : pour ces trois colonnes seulement, et sur les pots
     marqués 4-7 imprimés avant le 06/08/2026, l'indice du QR a été écrit par une table qui écrasait
     plusieurs doses sur un même chiffre. AUCUNE table de relecture ne peut leur rendre leur dose.
     Conduite à tenir : pour un pot marqué 4-7, l'atelier NE DOIT PAS se fier au QR pour sel rose,
     créatine et pipérine — il relit les milligrammes réels dans les propriétés de la commande
     (`_recharge` / `actives`), qui n'ont jamais été altérés. Le volume concerné est connu et petit
     (MNL-1001 à MNL-1006, plus les pots du 03 au 06/08). */
  var G3 = {
    caffeine:     [0, 50, 75, 100, 150, 200],
    theanine:     [0, 50, 100, 200, 300, 400],
    tyrosine:     [0, 250, 500, 1000, 1500, 2000],
    citrulline:   [0, 2, 3, 4, 6, 8],
    glycerol:     [0, 1, 1.5, 2, 3, 4],
    pinksalt:     [0, 200, 250, 300, 400, 500],
    betaalanine:  [0, 0.5, 1, 2, 3, 4],
    betaine:      [0, 50, 100, 200, 300, 400],
    taurine:      [0, 250, 500, 1000, 1500, 2000],
    creatine:     [0, 1, 2, 3],
    electrolytes: [0, 200, 400, 600, 1000, 1500],
    piperine:     [0, 0.5, 1, 1.5, 2]
  };

  /* ── ARCHIVE v2 (marqueur 2/3) — 5 crans, ère V1078 ───────────────────────────────────────────────
     ⚠ VALEURS D'ORIGINE, RESTAURÉES EN V1258. Le sel rose y avait été RÉÉCRIT par la V1171
     ([0,250,500,750,1000] → [0,200,300,400,500]) : une archive pré-convertie est une double conversion
     qui attend son heure, et c'est exactement ce qui s'est produit — le cran 1 (250 mg, une dose qui
     EXISTE encore aujourd'hui) ressortait à 200 mg. On garde l'archive BRUTE et on convertit à la
     lecture, une fois. La valeur restaurée est celle que citent, mot pour mot, les commentaires V1171
     du configurateur ET de recharge.html : ce n'est pas une reconstruction, c'est une restitution. */
  var G2 = {
    caffeine:     [0, 50, 100, 150, 200],
    theanine:     [0, 100, 200, 300, 400],
    tyrosine:     [0, 500, 1000, 1500, 2000],
    citrulline:   [0, 3, 4, 6, 8],
    glycerol:     [0, 3, 5, 6, 8],
    pinksalt:     [0, 250, 500, 750, 1000],         /* V1258 : ORIGINE restaurée (avant : réécrit par V1171) */
    betaalanine:  [0, 1, 2, 3, 4],
    betaine:      [0, 100, 200, 300, 400],
    taurine:      [0, 500, 1000, 1500, 2000],
    creatine:     [0, 3, 3, 4, 5],
    electrolytes: [0, 400, 600, 1000, 1500],
    piperine:     [0, 5, 8, 10, 15]
  };

  /* ── ARCHIVE v1 (marqueur 0/1) — 4 crans, ère pré-V1078 ───────────────────────────────────────────
     Mêmes remarques : sel rose restauré à [0,250,500,1000] (valeur citée par les deux commentaires
     V1171). Le cran glycérol 7 g est un vestige de la grille [0,6,9,12] ramenée sous 8 g en V1021 :
     il ne figure dans aucun univers à six crans et tombe donc, par « cran le plus proche, égalité au
     cran le plus BAS », sur le même indice que 6 g. C'est assumé et le banc l'affiche. */
  var G1 = {
    caffeine:     [0, 50, 100, 150, 200],
    theanine:     [0, 100, 200, 300, 400],
    tyrosine:     [0, 500, 1000, 1500, 2000],
    citrulline:   [0, 4, 6, 8],
    glycerol:     [0, 6, 7, 8],
    pinksalt:     [0, 250, 500, 1000],              /* V1258 : ORIGINE restaurée (avant : réécrit par V1171) */
    betaalanine:  [0, 2, 3, 4],
    betaine:      [0, 200, 300, 400],
    taurine:      [0, 500, 1000, 2000],
    creatine:     [0, 3, 4, 5],
    electrolytes: [0, 400, 800, 1500],
    piperine:     [0, 5, 10, 15]
  };

  /* ── UNIV — L'UNIVERS DE VALEURS À SIX CRANS DE L'ÈRE v3 ──────────────────────────────────────────
     LE RACCORD, et la seule table qui n'est pas une photographie de QR. Rôle : une dose v1/v2 est une
     VALEUR d'un univers ; UNIV lui rend son INDICE dans la géométrie à six crans, indice qui est
     ensuite valable pour toutes les générations ≥ 3 et donc reportable sur la grille courante.
     Construction, sans invention : pour chaque actif, c'est la grille v3 AU MOMENT où elle est passée
     à six crans (V1079), c.-à-d. AVANT les abaissements survenus pendant l'ère v3.
       glycérol  [0,2,3,5,6,8]        avant V1228 — c'est LA table décrétée par le fondateur en V1230
       sel rose  [0,200,250,500,750,1000]  avant V1171 — citée telle quelle par le commentaire V1171
       pipérine  [0,3,5,8,10,15]      avant V1134 — citée telle quelle par le commentaire V1134
       les huit autres : identiques à G3, leur valeur n'a pas bougé pendant l'ère v3.
     VÉRIFIÉ : chaque valeur non nulle des grilles v1 et v2 se retrouve DANS UNIV, à deux exceptions
     documentées (glycérol 7 g, électrolytes 800 mg), rattrapées par « cran le plus proche ».
     ⚠ CRÉATINE — LA SEULE VRAIE INVENTION, DONC LE SEUL VRAI ARBITRAGE. Aucun univers à six crans n'a
     jamais porté 4 et 5 g avec des valeurs distinctes : la V1131 n'a pas abaissé une échelle, elle a
     AJOUTÉ des crans bas (0/1/2/3 g) sous un actif qui était binaire (0 ou 3 g). Reporter un rang y
     serait le défaut de la preuve 1, en miroir : le cran 1 de la v1 vaut 3 g, et 3 g existe toujours.
     UNIV.creatine est donc la grille COURANTE elle-même, ce qui revient à convertir PAR LA VALEUR :
     tout cran non nul d'un pot ancien (3, 4 ou 5 g) rend 3 g, le dernier cran. Le pot en contenait au
     moins 3, le plafond EFSA est à 3, sommet → sommet : le client ne paie jamais un cran de plus pour
     une dose de moins. C'est le geste le moins mauvais ; il reste un ARBITRAGE et il est signalé
     comme tel dans ARBITRAGES ci-dessous.
     ⚠ PIPÉRINE — SIX CRANS EN HAUT, CINQ EN BAS. UNIV en a 6, la grille courante 5 (V1134). L'indice 5
     n'a pas de correspondant : il retombe sur le dernier cran (2 mg). Seule perte d'information de
     tout le dispositif ; elle ne peut que BAISSER une dose, jamais la monter. */
  var UNIV = {
    caffeine:     [0, 50, 75, 100, 150, 200],
    theanine:     [0, 50, 100, 200, 300, 400],
    tyrosine:     [0, 250, 500, 1000, 1500, 2000],
    citrulline:   [0, 2, 3, 4, 6, 8],
    glycerol:     [0, 2, 3, 5, 6, 8],
    pinksalt:     [0, 200, 250, 500, 750, 1000],
    betaalanine:  [0, 0.5, 1, 2, 3, 4],
    betaine:      [0, 50, 100, 200, 300, 400],
    taurine:      [0, 250, 500, 1000, 1500, 2000],
    creatine:     [0, 1, 2, 3],
    electrolytes: [0, 200, 400, 600, 1000, 1500],
    piperine:     [0, 3, 5, 8, 10, 15]
  };

  var GRIDS = { 1: G1, 2: G2, 3: G3, 4: G4, 5: G5, 6: G6 };

  /* Les points où une décision de PRIX a été prise faute de table fondateur. Exposés pour qu'une page
     d'atelier puisse les afficher au lieu de les enterrer dans un commentaire. */
  var ARBITRAGES = [
    { k: 'creatine', quoi: 'Aucun univers à six crans n\'a jamais porté 4 et 5 g. Tout cran non nul d\'un pot v1/v2 rend 3 g (dernier cran, plafond EFSA).' },
    { k: 'piperine', quoi: 'La V1134 a fait passer la grille de 6 à 5 crans. L\'indice 5 (15 mg) retombe sur le dernier cran (2 mg).' },
    { k: 'pinksalt', quoi: 'Marqueurs 4-7 imprimés avant le 06/08/2026 : encodeur et décodeur n\'avaient pas la même grille. Relire les mg réels de la commande, pas le QR.' },
    { k: 'creatine', quoi: 'Marqueurs 4-7 avant le 06/08/2026 : l\'encodeur écrasait 4 crans sur 1 ([0,3,3,3,3,3]). Relire les mg réels de la commande.' },
    { k: 'piperine', quoi: 'Marqueurs 4-7 avant le 06/08/2026 : l\'encodeur écrivait sur [0,3,5,8,10,15]. Relire les mg réels de la commande.' }
  ];

  /* ── Primitives ───────────────────────────────────────────────────────────────────────────────── */

  /* Cran le plus proche. ÉGALITÉ → CRAN LE PLUS BAS (`<` strict) : convention unique du dépôt,
     identique à idxP (labels.js, recharge.html) et à mnlPalierIndexOf (configurateur). La changer
     déplacerait des paliers de prix sur des doses à mi-chemin (glycérol 7 g, électrolytes 800 mg). */
  function nearestIndex(scale, v) {
    var best = 0, bd = Infinity;
    for (var i = 0; i < scale.length; i++) {
      var d = Math.abs(scale[i] - v);
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  }

  function gridOf(gen, k) {
    var g = GRIDS[gen] && GRIDS[gen][k];
    return (g && g.length) ? g : null;
  }

  function currentOf(k) {
    var c = GRIDS[GEN_COURANTE][k];
    return (c && c.length) ? c : null;
  }

  /* Plafond en vigueur = DERNIER cran de la grille courante. JAMAIS une constante écrite à la main :
     le jour où un plafond bouge dans G5, tout le dispositif suit et rien ne peut diverger en silence. */
  function capOf(k) {
    var c = currentOf(k);
    return c ? c[c.length - 1] : 0;
  }

  /* ── RÉGIME 1 — GÉNÉRATION CONNUE (QR ?r=) ────────────────────────────────────────────────────────
     `idx` est l'INDICE LU DANS LE CODE, jamais un indice reconstruit depuis une valeur : c'est la
     seule lecture qui survit aux archives non inversibles. `gen` vient de codeGen() : 4ᵉ chiffre ÷ 2
     pour un code HÉRITÉ de 16 chiffres (v1…v5), suffixe décimal pour un code ÉTENDU (v6 et au-delà).
     ⚠ V1271 : la borne haute est GEN_COURANTE, plus un « 5 » écrit à la main. Une génération inconnue
       (code d'un futur déploiement lu par un lecteur en retard) retombe sur la génération courante —
       même géométrie, report d'indice littéral : dégradé, jamais faux. */
  function fromIndex(k, idx, gen) {
    if (RETIRED[k]) return 0;
    var C = currentOf(k); if (!C) return 0;
    var i = parseInt(idx, 10);
    if (!(i > 0)) return C[0];                       /* cran 0 = actif absent du pot */
    var g = parseInt(gen, 10); if (!(g >= 1 && g <= GEN_COURANTE)) g = GEN_COURANTE;
    var G = gridOf(g, k) || C;
    if (i > G.length - 1) i = G.length - 1;          /* code plus long que la grille : sommet */
    var U = UNIV[k] || C;
    /* gen ≥ 3 : même géométrie qu'aujourd'hui → indice littéral.
       gen ≤ 2 : 4 ou 5 crans → on raccorde PAR LA VALEUR à l'univers à six crans. */
    var j = (g >= 3) ? i : nearestIndex(U, G[i]);
    if (j < 1) j = 1;                                /* un actif présent sur le pot ne retombe pas à 0 */
    if (j > C.length - 1) j = C.length - 1;          /* sommet → sommet, jamais un cran de plus */
    return C[j];
  }

  /* ── RÉGIME 2 — GÉNÉRATION INCONNUE (?f=, localStorage, _recharge, clavier) ────────────────────────
     Rend la dose du jour. Une valeur POSSIBLE aujourd'hui ressort INTACTE : son ancienneté n'est pas
     prouvée, et c'est aussi ce qui laisse au plafond de l'appelant toute sa force sur les formules
     NEUVES. Seule une valeur IMPOSSIBLE aujourd'hui a une ancienneté certaine ; on la replace sur son
     cran, on ne la tasse jamais sur le plafond. */
  function fromMg(k, mg) {
    if (RETIRED[k]) return 0;
    var C = currentOf(k); if (!C) return 0;
    var v = parseFloat(mg);
    if (!isFinite(v) || v <= 0) return 0;
    var cap = C[C.length - 1];
    if (v <= cap) return v;                          /* ① ambiguïté assumée → intacte */
    var U = UNIV[k] || C;
    var j = null, gsel;
    /* ② échelles de la PLUS RÉCENTE à la plus ancienne ; la première qui a pu porter v gagne.
       Les générations ≥ 3 d'abord (elles sont de la géométrie courante → indice littéral), puis UNIV,
       puis les grilles v2/v1 dont la valeur doit être raccordée par UNIV.
       ⚠ V1271 : on part de GEN_COURANTE − 1, pas d'un « 4 » écrit à la main. La génération COURANTE
         est déjà traitée par ① (v ≤ plafond ⇒ rendu intact), donc l'inclure ne changerait rien ; mais
         la génération qui vient de passer archive, elle, DOIT être consultée — c'est exactement celle
         qui porte la dose qu'on cherche à reconnaître. */
    for (var g = GEN_COURANTE - 1; g >= 3 && j === null; g--) {
      gsel = gridOf(g, k);
      if (gsel && gsel[gsel.length - 1] >= v) j = nearestIndex(gsel, v);
    }
    if (j === null && U[U.length - 1] >= v) j = nearestIndex(U, v);
    for (var g2 = 2; g2 >= 1 && j === null; g2--) {
      gsel = gridOf(g2, k);
      if (gsel && gsel[gsel.length - 1] >= v) j = nearestIndex(U, gsel[nearestIndex(gsel, v)]);
    }
    /* ③ au-dessus de TOUTE échelle jamais imprimée (caféine 250 mg, glycérol 12 g…) : ce n'est pas
       une étiquette, c'est du clavier ou un lien forgé. Dernier cran — sommet, jamais au-delà. */
    if (j === null) j = C.length - 1;
    if (j < 1) j = 1;
    if (j > C.length - 1) j = C.length - 1;
    return C[j];
  }

  /* Applique fromMg aux douze clés d'un objet de doses. Idempotent. */
  function mapMg(d) {
    if (!d || typeof d !== 'object') return d;
    var out = {};
    Object.keys(d).forEach(function (k) { out[k] = (currentOf(k) ? fromMg(k, d[k]) : 0); });
    return out;
  }

  /* Indice du cran le plus proche dans la grille COURANTE — pour les surfaces qui ENCODENT un QR
     (idxP) ou qui raisonnent en crans (bilan.html). Une seule définition pour tout le dépôt. */
  function indexOf(k, dose) {
    var C = currentOf(k); if (!C) return 0;
    if (RETIRED[k]) return 0;
    return nearestIndex(C, parseFloat(dose) || 0);
  }

  /* Dose de la grille courante à un indice donné (bornée). */
  function doseAt(k, i) {
    var C = currentOf(k); if (!C || RETIRED[k]) return 0;
    var j = parseInt(i, 10); if (!(j > 0)) return C[0];
    if (j > C.length - 1) j = C.length - 1;
    return C[j];
  }

  /* ── LE FORMAT DU CODE COMPACT — V1271. QUATRE SURFACES, UNE SEULE GRAMMAIRE. ─────────────────────
     Deux décodeurs (configurateur, recharge.html), deux encodeurs (api/labels.js, recharge.html) et un
     renifleur d'URL doivent s'accorder au chiffre près sur ce qu'est un code. Les faire s'accorder par
     recopie, c'est le mode de panne que ce fichier existe pour supprimer : la V1256 est née d'un
     encodeur resté à « 6 » pendant que le décodeur passait à 8. Ces trois fonctions sont donc LA
     grammaire, et personne ne la réécrit.

     GÉOMÉTRIE DU CODE, position par position — FIGÉE PAR LES QR IMPRIMÉS :
       0        format          1  intensité       2  saveur
       3        2×(gen−1) + sansStim, SATURÉ ET GELÉ À 8/9 (voir l'en-tête V1271)
       4…15     les douze indices de cran, dans l'ordre de KEYS
       16…      V1271 : la génération en décimal, PRÉSENTE UNIQUEMENT à partir de la v6.
     Le discriminant est la LONGUEUR. 16 ⇒ hérité, ≥ 17 ⇒ étendu. Rien d'autre n'est examiné, et c'est
     ce qui rend les deux langages incapables de se confondre. */
  var CODE_LEN_HERITE = 16;

  /* Vrai si `s` a la FORME d'un code — douze indices précédés de quatre chiffres d'en-tête, plus un
     éventuel suffixe. Ne dit rien de la validité de la génération : c'est le travail de codeGen. */
  function codeForme(s) { return /^[0-9]{16}[0-9]*$/.test(s); }

  /* Les 16 chiffres hérités, sans le suffixe. '' si l'entrée n'a pas la forme d'un code.
     C'EST AUSSI, LITTÉRALEMENT, CE QU'UN LECTEUR ANCIEN VOIT d'un code étendu — le renifleur non
     ancré de recharge.html capture exactement cette tranche. La dégradation n'est donc pas un espoir :
     c'est la même fonction, éprouvée par le banc sur les deux surfaces. */
  function codeBase(code) {
    var s = String(code == null ? '' : code);
    return codeForme(s) ? s.slice(0, CODE_LEN_HERITE) : '';
  }

  /* La génération portée par un code. 0 = ce n'est pas un code lisible — un décodeur qui reçoit 0
     doit rendre null, JAMAIS deviner.
       len = 16  → héritée du 4ᵉ chiffre : ⌊c3 ÷ 2⌋ + 1, soit v1 (0/1) … v5 (8/9).
       len ≥ 17  → lue en clair à la fin. REFUSÉE si < 6 : une génération que le 4ᵉ chiffre sait déjà
                   exprimer n'a rien à faire en suffixe, et l'accepter ouvrirait deux écritures pour la
                   même chose — donc, tôt ou tard, deux lectures. */
  function codeGen(code) {
    var s = String(code == null ? '' : code);
    if (!codeForme(s)) return 0;
    if (s.length === CODE_LEN_HERITE) return Math.floor(parseInt(s.charAt(3), 10) / 2) + 1;
    var g = parseInt(s.slice(CODE_LEN_HERITE), 10);
    return (isFinite(g) && g >= 6) ? g : 0;
  }

  /* Estampille les 16 chiffres d'en-tête avec la génération en cours. C'EST LE SEUL POINT D'ÉCRITURE
     du format étendu : les deux encodeurs l'appellent, aucun ne concatène à la main.
     Tant que GEN_COURANTE ≤ 5, elle rend le code hérité INCHANGÉ — le passage au format étendu n'est
     donc pas un « avant/après » dans le code, c'est une conséquence de la génération. */
  function codeAvecGen(base16) {
    var s = String(base16 == null ? '' : base16);
    if (!/^[0-9]{16}$/.test(s)) return s;
    return (GEN_COURANTE >= 6) ? (s + String(GEN_COURANTE)) : s;
  }

  /* ── TÉMOIN POSITIF EMBARQUÉ ──────────────────────────────────────────────────────────────────────
     Empreinte calculée SUR LES TABLES, à l'exécution. Deux copies de ce fichier qui n'affichent pas la
     même chaîne ne portent pas la même vérité — et ça se constate depuis la console d'une page, sans
     accès au dépôt. selfTest() rejoue huit conversions dont la réponse est connue et documentée
     ailleurs dans le dépôt (tables du fondateur V1230/V1236/V1246, correctifs V1253/V1255) : il rend
     `true` ET la liste des cas, pour qu'un contrôle voie ce qui a été vérifié, pas seulement un vert. */
  /* Deux mélangeurs indépendants (djb2 ⊕ et sdbm +) plus la longueur du texte. Ce n'est PAS une
     empreinte cryptographique et ça n'a pas à l'être : elle ne défend pas contre un adversaire, elle
     défend contre une divergence ACCIDENTELLE entre deux copies d'un même fichier. Deux hachages de
     familles différentes plus la longueur rendent une collision fortuite hors de portée. */
  function hash2(src) {
    var h1 = 5381, h2 = 52711;
    for (var i = 0; i < src.length; i++) {
      var c = src.charCodeAt(i);
      h1 = ((h1 * 33) ^ c) >>> 0;
      h2 = (((h2 * 31) >>> 0) + c) >>> 0;
    }
    return ('0000000' + h1.toString(16)).slice(-8) + ('0000000' + h2.toString(16)).slice(-8);
  }

  /* ── SIGNATURE — L'EMPREINTE QUI COUVRE L'ALGORITHME, PAS SEULEMENT LES TABLES ────────────────────
     C'est LA garantie de la V1259 contre « les deux exemplaires ont divergé et personne ne l'a vu ».
     Elle est calculée sur `String(MNL_DOSE_MODULE)`, c'est-à-dire le TEXTE SOURCE COMPLET de tout ce
     qui est entre les accolades de la fabrique : les cinq grilles, UNIV, nearestIndex, fromIndex,
     fromMg, les bornes, et jusqu'aux commentaires internes. Une ligne d'algorithme réécrite d'un côté
     et pas de l'autre change la SIGNATURE ; FINGERPRINT, lui, ne bougeait pas — c'est précisément le
     trou par lequel les onze implémentations divergentes sont passées.
     CE QU'ELLE N'INCLUT PAS, VOLONTAIREMENT : le commentaire d'en-tête (hors fabrique) et les retours
     chariot. Une copie transportée en CRLF reste conforme ; une note de prose ajoutée d'un seul côté
     ne déclenche pas de fausse alerte. Ce qui doit être identique, c'est le COMPORTEMENT.
     ⚠ SI UNE DES DEUX ORIGINES SE MET À MINIFIER CE FICHIER, la SIGNATURE divergera. C'est voulu :
     un fichier minifié d'un seul côté est exactement le genre de divergence qu'on veut voir crier.
     Aujourd'hui ni le CDN Shopify (assets/) ni Vercel (public/) ne transforment ce fichier. */
  function sourceSignature() {
    var src;
    try { src = String(MNL_DOSE_MODULE); } catch (_) { return 'MNLS-INDISPONIBLE'; }
    if (typeof src !== 'string' || src.indexOf('fromIndex') < 0) return 'MNLS-ILLISIBLE';
    src = src.replace(/\r/g, '');
    return 'MNLS-' + hash2(src) + '-' + src.length;
  }

  function fingerprint() {
    var s = 'V1271|' + KEYS.join(',') + '|';
    /* V1271 : les générations sont ÉNUMÉRÉES DEPUIS GRIDS, plus depuis une liste écrite à la main.
       Une archive ajoutée sans que l'empreinte la couvre serait une archive que rien ne surveille. */
    Object.keys(GRIDS).sort(function (a, b) { return a - b; }).forEach(function (g) {
      s += g + ':' + KEYS.map(function (k) { return (GRIDS[g][k] || []).join('-'); }).join(';') + '|';
    });
    s += 'U:' + KEYS.map(function (k) { return (UNIV[k] || []).join('-'); }).join(';');
    var h = 5381;
    for (var i = 0; i < s.length; i++) { h = ((h * 33) ^ s.charCodeAt(i)) >>> 0; }
    return 'MNLD-' + ('0000000' + h.toString(16)).slice(-8) + '-' + s.length;
  }

  function selfTest() {
    var cas = [
      /* [libellé, valeur obtenue, valeur attendue, d'où vient l'attendu] */
      /* PREUVE 1 — l'invariant « la VALEUR n'a pas bougé » se lit désormais sur la citrulline : 3 g au
         cran 1 de la v2, 3 g au cran 2 d'aujourd'hui. La tyrosine ne peut plus le porter depuis la
         V1271 (son échelle a été divisée par deux) ; elle porte à la place le contrôle de la
         conversion à indice constant, ligne suivante. */
      ['v2 citrulline cran 1 = 3 g, dose inchangée depuis', fromIndex('citrulline', 1, 2), 3, 'preuve 1'],
      /* ⚠ CE CAS COUVRE GRIDS[2].tyrosine, ET C'EST SA RAISON D'ÊTRE. Il portait la preuve 1 jusqu'à
         la V1271 ; sa VALEUR attendue a changé (500 → 250), pas son utilité. Le retirer aurait rendu
         MUETTE la mutation `GRIDS[2].tyrosine` de banc-theme/banc-v1267.cjs §1 — un contrôle qu'on
         ne sait plus faire échouer n'est plus un contrôle. On change l'attendu, on ne supprime pas la
         couverture. */
      ['v2 tyrosine cran 1 (500 mg) → 250 mg depuis la V1271', fromIndex('tyrosine', 1, 2), 250, 'V1271 — couvre GRIDS[2].tyrosine'],
      ['V1271 — v5 tyrosine cran 5 (2000 mg) → 1000 mg, indice 5 conservé', fromIndex('tyrosine', 5, 5), 1000, 'V1271, conversion à indice constant'],
      ['V1271 — v5 tyrosine cran 3 (1000 mg) → 500 mg, indice 3 conservé', fromIndex('tyrosine', 3, 5), 500, 'V1271 : 1000 mg n\'est PAS « déjà conforme »'],
      ['V1271 — ?f= tyrosine 2000 mg (impossible aujourd\'hui) → 1000 mg', fromMg('tyrosine', 2000), 1000, 'V1271, cran 5 de l\'échelle sortante'],
      ['v2 glycérol cran 1 (3 g)', fromIndex('glycerol', 1, 2), 1, 'table fondateur V1230 → indice 2, puis V1236'],
      ['v1 glycérol cran 1 (6 g)', fromIndex('glycerol', 1, 1), 2, 'table fondateur V1230 (6→3), puis V1236'],
      ['v4 taurine cran 4 (1500 mg)', fromIndex('taurine', 4, 4), 750, 'V1253 / lbMigrateDose'],
      ['v1 créatine cran 1 (3 g)', fromIndex('creatine', 1, 1), 3, 'arbitrage créatine (sommet → sommet)'],
      ['?f= théanine 300 mg', fromMg('theanine', 300), 150, 'V1236 / lbMigrateDose'],
      ['?f= pipérine 5 mg', fromMg('piperine', 5), 1, 'univers pré-V1134, cran 2'],
      ['?f= sel rose 750 mg', fromMg('pinksalt', 750), 400, 'univers pré-V1171, cran 4'],
      ['?f= taurine 1000 mg (dose du jour) intacte', fromMg('taurine', 1000), 1000, 'ambiguïté assumée'],
      ['bétaïne toujours nulle', fromIndex('betaine', 5, 2) + fromMg('betaine', 400), 0, 'V1135'],
      /* ── V1271 : LE FORMAT DU CODE. Trois cas qui ne dépendent d'AUCUNE valeur de grille — ils
         restent vrais quel que soit le plafond du jour, et c'est voulu : un autotest qui bouge à
         chaque abaissement de dose finit par être ajusté au lieu d'être lu. ── */
      ['code HÉRITÉ (16 ch.) : génération au 4ᵉ chiffre', codeGen('1008000000000000'), 5, 'V1078 — 8/9 = v5'],
      ['code ÉTENDU (17 ch.) : génération en suffixe', codeGen('1008000000000000' + '6'), 6, 'V1271'],
      ['suffixe < 6 REFUSÉ (les deux langages ne se chevauchent pas)', codeGen('10080000000000005'), 0, 'V1271'],
      ['le tronquage d\'un lecteur ancien rend le code hérité intact', codeBase('1008000000000000' + '6'), '1008000000000000', 'V1271 — dégradation'],
      ['v5 taurine cran 4 → report d\'indice littéral', fromIndex('taurine', 4, 5), doseAt('taurine', 4), 'gen ≥ 3'],
      ['v6 (génération courante) : cran lu = cran rendu', fromIndex('citrulline', 3, GEN_COURANTE), doseAt('citrulline', 3), 'V1271']
    ];
    var ko = cas.filter(function (c) { return c[1] !== c[2]; });
    return { ok: ko.length === 0, total: cas.length, echecs: ko, cas: cas,
             empreinte: fingerprint(), signature: sourceSignature() };
  }

  /* ── LE POINT DE CONTRÔLE UNIQUE, APPELÉ PAR TOUT CE QUI VEUT SAVOIR SI LE MODULE EST SAIN ────────
     `attendue` = la SIGNATURE de l'AUTRE exemplaire, quand l'appelant a pu se la procurer (la page
     d'atelier charge les deux origines ; le vérificateur de dépôt charge les deux fichiers). Quand
     l'appelant ne peut pas la connaître — cas d'une page client, qui ne charge qu'une copie —, il
     n'en passe pas, et `verifier()` ne rend alors que l'autotest : c'est une garantie plus faible,
     assumée, et c'est pour ça que les deux autres garanties (dépôt + atelier) existent.
     RÈGLE : personne ne réimplémente ce diagnostic. Une surface qui veut savoir appelle verifier(). */
  function verifier(attendue) {
    var sig = sourceSignature();
    var st = selfTest();
    var miroir = !attendue ? 'inconnu' : (String(attendue) === sig ? 'identique' : 'DIVERGENT');
    var motifs = [];
    if (!st.ok) motifs.push('autotest en échec (' + st.echecs.length + '/' + st.total + ')');
    if (miroir === 'DIVERGENT') motifs.push('les deux exemplaires du module ne portent pas la même signature');
    if (sig.indexOf('MNLS-') !== 0 || sig === 'MNLS-INDISPONIBLE' || sig === 'MNLS-ILLISIBLE') motifs.push('signature illisible (source transformée ?)');
    return {
      ok: motifs.length === 0,
      motifs: motifs,
      version: 'V1271',
      signature: sig,
      empreinte: fingerprint(),
      autotest: st,
      miroir: miroir
    };
  }

  return {
    VERSION: 'V1271',
    DATE: '2026-08-06',
    /* Les deux chemins qui doivent porter le MÊME texte. Exposés pour qu'un diagnostic les nomme au
       lieu de les paraphraser — et pour que la page d'atelier n'invente pas ses propres URL. */
    MIROIRS: ['assets/mnl-dose-conversion.js', 'backend-vercel/public/mnl-dose-conversion.js'],
    KEYS: KEYS,
    RETIRED: RETIRED,
    CURRENT: G6,
    /* V1271 : la génération qu'on FABRIQUE aujourd'hui. Toute surface qui écrit un code, borne une
       génération ou boucle sur les archives lit CETTE valeur — jamais un « 5 » écrit à la main. */
    GEN_COURANTE: GEN_COURANTE,
    GRIDS: GRIDS,
    UNIV: UNIV,
    ARBITRAGES: ARBITRAGES,
    nearestIndex: nearestIndex,
    capOf: capOf,
    gridOf: gridOf,
    fromIndex: fromIndex,
    fromMg: fromMg,
    mapMg: mapMg,
    indexOf: indexOf,
    doseAt: doseAt,
    /* ── LA GRAMMAIRE DU CODE COMPACT (V1271) — les quatre surfaces l'appellent, aucune ne la récrit.
       codeGen  : décodeurs (configurateur, recharge.html) + banc — 0 ⇒ refuser, jamais deviner.
       codeAvecGen : encodeurs (api/labels.js, recharge.html) — SEUL point d'écriture du suffixe.
       codeBase / CODE_LEN_HERITE : renifleurs d'URL, et ce que voit un lecteur ANCIEN. */
    CODE_LEN_HERITE: CODE_LEN_HERITE,
    codeForme: codeForme,
    codeBase: codeBase,
    codeGen: codeGen,
    codeAvecGen: codeAvecGen,
    /* FINGERPRINT = les TABLES seules (héritée V1258, conservée : elle dit QUEL genre de divergence).
       SIGNATURE  = tables + ALGORITHME (V1259). C'est celle-ci qui fait foi entre deux exemplaires. */
    FINGERPRINT: fingerprint(),
    fingerprint: fingerprint,
    SIGNATURE: sourceSignature(),
    signature: sourceSignature,
    selfTest: selfTest,
    verifier: verifier
  };
}));
